import { Form, FormProps } from 'antd';
import { useEffect } from 'react';

import useFormInitialSet from '@/hooks/useFormInitialSet';
import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import { useSelectPropsWithInitial } from '@/hooks/useSelectWithInitialValue';
import { FailReportItem } from '@/model/FailReport/FailReportItem';

const useFailReportItemForm = (formProps: FormProps, editingItem?: FailReportItem | null) => {
  // Precarga los valores iniciales del formulario cuando se edita un item existente.
  useFormInitialSet(formProps.form, editingItem ?? undefined, [], {
    withoutInitial: !editingItem,
  });

  // 0: product, 1: unidad, 2: device
  const productType = Form.useWatch('product_type', formProps.form);
  // Select de productos/unidades/dispositivos según el tipo seleccionado.
  const { selectProps: productsSelectProps } = useSelectScrollSearch({
    resource: 'elements',
    optionLabel: item => item.name,
    optionValue: item => item.id,
    queryOptions: {
      enabled: productType !== undefined,
    },
    filters: [
      {
        value: productType,
        operator: 'eq' as const,
        field: 'type',
      },
    ],
  });
  const productsSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: productsSelectProps,
    resource: 'elements',
    initialValue: editingItem?.product,
    labelKey: 'name',
    valueKey: productType === 1 ? 'serial_number_box' : 'id',
    disabled: productType === undefined,
  });
  // Select de números de serie (unidades o dispositivos serializables).
  const { selectProps: serialNumbersSelectProps } = useSelectScrollSearch({
    resource: 'elements',
    optionLabel: item => item.name,
    optionValue: item => (productType === 1 ? item.serial_number_box : item.id),
    queryOptions: {
      enabled: productType !== undefined && (productType === 1 || productType === 2),
      queryKey: ['elements', { type: productType, serializable: true }],
    },
    filters: [
      {
        value: productType,
        operator: 'eq' as const,
        field: 'type',
      },
      { value: true, operator: 'eq' as const, field: 'serializable' },
    ],
  });
  const serialNumbersSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: serialNumbersSelectProps,
    resource: 'elements',
    initialValue: productType == 1 ? editingItem?.unidad : editingItem?.device,
    labelKey: 'name',
    valueKey: productType === 1 ? 'serial_number_box' : 'id',
    disabled: productType === undefined,
    meta: { type: productType, serializable: true },
  });

  // Resetea campos dependientes cuando cambia el tipo de producto.
  useEffect(() => {
    if (!editingItem) {
      formProps.form?.resetFields(['product', 'unidad', 'device']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType]);

  return {
    productType,
    productsSelectProps: productsSelectPropsWithInitials,
    serialNumbersSelectProps: serialNumbersSelectPropsWithInitials,
  };
};

export default useFailReportItemForm;
